import { Fragment } from 'react';
import styled, { keyframes } from 'styled-components';
import { media } from '../../helpers/responsive';
import { useTickerContext } from '../../helpers/hooks/useTickerContext';
import { numFormatter } from '../../helpers/utils';
import type { TickerContentDataType } from '../../types/widgets';
import { TooltipHelper } from '../tooltipHelper';
import { TickerDivider } from './divider';
import { renderIndicator } from './utils';

const TickerBanner = () => {
  const { tickerData, isLoading } = useTickerContext() as {
    tickerData: TickerContentDataType[];
    isLoading: boolean;
  };
  const showTickerBanner = tickerData && tickerData.length > 0;
  const showLoadingState = isLoading;

  // Show loading skeleton when tickers are being fetched independently
  if (showLoadingState) {
    return (
      <Wrapper className="show-background">
        <TickerSkeleton />
        <TickerDivider />
        <TickerSkeleton />
        <TickerDivider />
        <TickerSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={showTickerBanner ? 'show-background' : ''}>
      {showTickerBanner
        ? tickerData.map((d: TickerContentDataType, i: number) => (
            <Fragment key={i}>
              <div className="ticker">
                <span className="label">{d.name}</span>
                <TooltipHelper
                  tooltipText={`${d.value_formatter} ${formatNumber(parseInt(d.value))}`}
                  placement="bottom"
                >
                  <span className="value">
                    {`${d.value_formatter ?? ''} ${numFormatter(parseInt(d.value))}`}
                  </span>
                </TooltipHelper>
                {renderIndicator(d.indication)}
              </div>
              {i !== tickerData.length - 1 && <TickerDivider />}
            </Fragment>
          ))
        : null}
    </Wrapper>
  );
};

// Loading skeleton for tickers
const TickerSkeleton = () => (
  <SkeletonWrapper>
    <SkeletonLabel />
    <SkeletonValue />
  </SkeletonWrapper>
);

export default TickerBanner;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

const SkeletonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SkeletonLabel = styled.div`
  width: 80px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 25%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonValue = styled.div`
  width: 60px;
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(13, 138, 106, 0.2) 25%,
    rgba(13, 138, 106, 0.4) 50%,
    rgba(13, 138, 106, 0.2) 75%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const Wrapper = styled.div`
  height: 35px;
  width: 100%;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  padding-left: 50px;
  padding-right: 50px;
  .ticker {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 23.4px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    .label {
      margin-right: 10px;
      opacity: 0.7;
      font-weight: 600;
    }
    .value {
      cursor: default;
      color: #0d8a6a;
      font-weight: 800;
    }
    .up {
      color: #0d8a6a;
    }
    .down {
      color: #cb2e2e;
    }
  }
  .dot {
    margin-left: 2.5rem;
    margin-right: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      circle {
        fill: ${({ theme }) => theme.ticker.circle_fill};
      }
    }
  }
  &.show-background {
    background: ${({ theme }) => theme.ticker.background_color};
    border-bottom: ${({ theme }) => theme.ticker.border_bottom};
  }

  ${media.down.lg} {
    height: 25px;
    .ticker {
      font-size: 14px;
      line-height: 25px;
    }
  }

  ${media.down.xs} {
    padding: 0 16px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
    scrollbar-width: none;
    .ticker { font-size: 12px; flex-shrink: 0; }
    .dot { margin: 0 1rem; }
  }

  ${media.up.xxl} {
    height: 40px;
    .ticker { font-size: 1.1rem; }
  }
`;
